import { useId, type ReactNode } from 'react';
import { TextField } from '@heroui/react/textfield';
import { Label } from '@heroui/react/label';
import { Description } from '@heroui/react/description';
import { FieldError } from '@heroui/react/field-error';
import { cn } from '@/utils/cn';
import { FieldLabelContext } from './field-context';

/**
 * Label + control + hint/error wrapper, built on HeroUI's TextField.
 *
 * TextField provides the field context, so the Label, Description and
 * FieldError are wired to the control it contains — ids and
 * `aria-describedby` are generated for us.
 *
 * Most of the time you don't need this: `<Input label=… hint=… error=… />`
 * renders the same stack. Reach for `Field` when wrapping a control that is
 * not a DS Input (a date picker, a custom widget).
 */
export interface FieldProps {
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  /** @deprecated Ids are generated by HeroUI; this is no longer needed. */
  htmlFor?: string;
}

export function Field({
  label,
  required,
  disabled,
  hint,
  error,
  children,
  className,
}: FieldProps) {
  const labelId = useId();
  const descriptionId = useId();
  const showHint = !!hint && !error;
  return (
    <TextField
      isRequired={required}
      isDisabled={disabled}
      isInvalid={!!error}
      className={cn('flex w-full flex-col gap-1.5', className)}
    >
      {label && <Label id={labelId}>{label}</Label>}
      <FieldLabelContext.Provider
        value={{
          labelId: label ? labelId : undefined,
          descriptionId: showHint ? descriptionId : undefined,
        }}
      >
        {children}
      </FieldLabelContext.Provider>
      {showHint && <Description id={descriptionId}>{hint}</Description>}
      {error && <FieldError>{error}</FieldError>}
    </TextField>
  );
}
